/** @var {BookingCalendar[]} List of all active calendars */
import axios, {AxiosResponse} from "axios";
import {initTooltips} from "./functions";
import {SubTypeData, TypeData} from "./bookingForm";

export interface CalendarConfiguration {
	year: string,
	month: string,
	day: string,
	selectedDate: string,
	type: number | TypeData,
	subType: number | SubTypeData,
}

export interface TimeValue {
	time: string,
	slots: number
}

export let calendarObjects: BookingCalendar[] = [];

/**
 * Initialize calendar functionality
 *
 * @param {Element|Node} calendarObject
 */
export default function initCalendar(calendarObject: BookingCalendar | null = null) {
	const calendarElements: NodeListOf<HTMLDivElement> = document.querySelectorAll('.calendar');
	calendarElements.forEach(calendar => {
		// Skip already initialized calendars
		if (calendar.dataset.initialized === 'true') {
			return;
		}

		calendarObjects.push(new BookingCalendar(calendar));
	});
}

class BookingCalendar {
	public obj: HTMLDivElement;
	public configuration: CalendarConfiguration;
	private preset: number[] = [1];
	private timeCount: number = 0;

	/**
	 * Calendar constructor
	 *
	 * @param {HTMLElement|Element} obj
	 */
	constructor(obj: HTMLDivElement) {
		this.init(obj)
	}

	/**
	 * Initializes the calendar HTML element
	 *
	 * Initializes all event listeners, set-up config, etc..
	 *
	 * @param {HTMLElement|Element} obj
	 */
	init(obj: HTMLDivElement) {
		this.obj = obj;
		initTooltips(this.obj);
		let config = this.obj.dataset.configuration.replaceAll('\\"', '"');
		this.configuration = JSON.parse(config);

		this.obj.dataset.initialized = 'true';

		(this.obj.querySelectorAll('.changeDate') as NodeListOf<HTMLElement>).forEach(btn => {
			btn.addEventListener('click', () => {
				if (btn.dataset.year) {
					this.configuration.year = btn.dataset.year;
				}
				if (btn.dataset.month) {
					this.configuration.month = btn.dataset.month;
				}
				if (btn.dataset.day) {
					this.configuration.day = btn.dataset.day;
				}
				this.reload();
				this.timeCount = 0;
				this.emitEvent('calendarDateChange');
				this.emitEvent('calendarTimeChange');
			});
		});

		(this.obj.querySelectorAll('.dateSelect') as NodeListOf<HTMLInputElement>).forEach(input => {
			input.addEventListener('change', () => {
				const date = input.value;
				const [year, month, day] = date.split('-');
				this.configuration.year = year;
				this.configuration.month = month;
				this.configuration.day = day;
				this.configuration.selectedDate = date;
				this.reload();
				this.timeCount = 0;
				this.emitEvent('calendarDateChange');
				this.emitEvent('calendarTimeChange');
			});
		});

		(this.obj.querySelectorAll('.timeSelect') as NodeListOf<HTMLInputElement>).forEach(input => {
			input.addEventListener('change', () => {
				// First selected time
				if (this.timeCount === 0) {
					console.log('first input', input, this.preset);
					// Apply preset
					let slot = true;
					let currentInput: HTMLInputElement | null = input;
					let stop = false;
					for (const count of this.preset) {
						console.log(count, slot);
						for (let i = 0; i < count; ++i) {
							console.log(currentInput);
							if (slot) {
								if (parseInt(currentInput.dataset.available) > 0) {
									currentInput.checked = true;
								}
							}
							currentInput = this.getNextSiblingInput(currentInput);
							if (!currentInput) {
								console.log('stop');
								stop = true;
								break;
							}
						}
						if (stop) {
							break;
						}
						slot = !slot;
					}
				}

				this.timeCount = this.getSelectedTimes().length;

				this.emitEvent('calendarTimeChange');
			});
		});
	}

	/**
	 * Reloads the whole calendar using an AJAX call
	 */
	reload(): void {
		this.obj.querySelector('.loading').classList.remove('d-none');
		axios.post('/calendar', this.configuration)
			.then((response: AxiosResponse<string>) => {
				const wrapper = document.createElement('div');
				wrapper.innerHTML = response.data;
				const newCalendar: HTMLDivElement = wrapper.querySelector('.calendar');
				this.obj.parentNode.replaceChild(newCalendar, this.obj);
				this.init(newCalendar);
			})
			.catch(response => {
				console.error(response);
			})
			.finally(() => {
				this.timeCount = 0;
				this.obj.querySelector('.loading').classList.add('d-none');
				this.emitEvent('calendarReload');
			});
	}

	/**
	 * Emit a DOM event, that other scripts can listen to
	 *
	 * @param {string} eventName
	 * @param {boolean} bubbles
	 */
	emitEvent(eventName: string, bubbles: boolean = true) {
		console.log('Emitting event', eventName);
		const e = new Event(eventName, {bubbles});
		console.log(e);
		this.obj.dispatchEvent(e);
	}

	/**
	 * Gets the selected date
	 * @returns {string|null} Date string or null if no date is selected
	 */
	getSelectedDate(): string | null {
		const selectedInput: HTMLInputElement = this.obj.querySelector('.dateSelect:checked');
		if (selectedInput) {
			return selectedInput.value;
		}
		return null;
	}

	/**
	 * Gets the selected date
	 * @returns {string|null} Date string or null if no date is selected
	 */
	getSelectedDateFormatted(): string | null {
		const selectedInput: HTMLInputElement = this.obj.querySelector('.dateSelect:checked');
		if (selectedInput) {
			return selectedInput.dataset.formatted;
		}
		return null;
	}

	/**
	 * Gets the selected time
	 * @returns {{time:string,slots:number}[]} - Time string or null if no time is selected
	 */
	getSelectedTimesInfo(): TimeValue[] {
		let values: TimeValue[] = [];
		const selectedInputs: NodeListOf<HTMLInputElement> = this.obj.querySelectorAll('.timeSelect:checked');
		if (selectedInputs.length > 0) {
			selectedInputs.forEach(input => {
				values.push({
					time: input.value,
					slots: parseInt(input.dataset.available ?? '0')
				});
			});
			return values;
		}
		return [];
	}

	/**
	 * Gets the selected time
	 * @returns {string[]} - Time string or null if no time is selected
	 */
	getSelectedTimes(): string[] {
		let values: string[] = [];
		const selectedInputs: NodeListOf<HTMLInputElement> = this.obj.querySelectorAll('.timeSelect:checked');
		if (selectedInputs.length > 0) {
			selectedInputs.forEach(input => {
				values.push(input.value);
			});
			return values;
		}
		return [];
	}

	setPreset(preset: string): void {
		const parts = preset.split('-');
		this.preset = [];
		parts.forEach(part => {
			this.preset.push(parseInt(part));
		});
		console.log(this.preset);
	}

	private getNextSiblingInput(input: HTMLInputElement): HTMLInputElement | null {
		let elem: HTMLElement = input;
		do {
			elem = elem.nextElementSibling as HTMLElement;
			if (elem instanceof HTMLInputElement) {
				return elem;
			}
		} while (elem);
		return null;
	}
}
